<script setup lang="ts">
    import { computed, onMounted, watch, shallowReactive, Ref } from 'vue'
    import { useRoute } from 'vue-router'
    import { guid } from '@common/Guid'

    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"
    import CostSubtotalEditor from "@Quest/Costs/Components/CostSubtotalEditor.vue"
    import ExpenseAccountEditor from "@Quest/Costs/Components/ExpenseAccountEditor.vue"

    import type {
        CreateCostSubtotalEventPayload,
        RenameCostSubtotalEventPayload
    } from '@Quest/Costs/Components/CostSubtotalEditor'

    import type {
        CreateExpenseAccountEventPayload,
        UpdateCostAccountEventPayload
    } from '@Quest/Costs/Components/ExpenseAccountEditor'

    import
        {
            CostSubtotalEditorViewModel
        } from '@Quest/Costs/Components/CostSubtotalEditor'

    import
        {
            ExpenseAccountEditorViewModel
        } from '@Quest/Costs/Components/ExpenseAccountEditor'

    import type {
        CostAccountReadModel,
        CostSubtotalReadModel,
        CostsReadModel
    } from '@Quest/Costs/readmodels'

    import
        {
            useManageCostsPageQueryV1
        } from '@Quest/Costs/queries-vue-composables'

    import
        {
            useCreateExpenseAccountCommandV1,
            useUpdateCostAccountCommandV1,
            useDeleteExpenseAccountCommandV1,
            useCreateCostSubtotalCommandV1,
            useRenameCostSubtotalCommandV1,
            useDeleteCostSubtotalCommandV1
        } from '@Quest/Costs/commands-vue-composables'

    /* -------------------- Route -------------------- */

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    /* -------------------- Editors -------------------- */

    const costSubtotalEditor = shallowReactive(new CostSubtotalEditorViewModel())
    const expenseAccountEditor = shallowReactive(new ExpenseAccountEditorViewModel())

    /* -------------------- Query -------------------- */

    const {
        runManageCostsPageQueryV1,
        modelManageCostsPageQueryV1
    } = useManageCostsPageQueryV1(workspaceId, strategyId)

    const rm = modelManageCostsPageQueryV1 as Ref<CostsReadModel | null>

    /* -------------------- Derived State -------------------- */

    const costs = computed(() => rm.value?.Costs ?? [])
    const subtotals = computed(() => rm.value?.Subtotals ?? [])
    const standaloneCosts = computed<CostAccountReadModel[]>(() =>
        costs.value.filter(a => a.IsStandalone)
    )

    /* -------------------- Commands -------------------- */

    const {
        invokeCreateExpenseAccountCommandV1,
        succeedCreateExpenseAccountCommandV1
    } = useCreateExpenseAccountCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateCostAccountCommandV1,
        succeedUpdateCostAccountCommandV1
    } = useUpdateCostAccountCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteExpenseAccountCommandV1,
        succeedDeleteExpenseAccountCommandV1
    } = useDeleteExpenseAccountCommandV1(workspaceId, strategyId)

    const {
        invokeCreateCostSubtotalCommandV1,
        succeedCreateCostSubtotalCommandV1
    } = useCreateCostSubtotalCommandV1(workspaceId, strategyId)

    const {
        invokeRenameCostSubtotalCommandV1,
        succeedRenameCostSubtotalCommandV1
    } = useRenameCostSubtotalCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteCostSubtotalCommandV1,
        succeedDeleteCostSubtotalCommandV1
    } = useDeleteCostSubtotalCommandV1(workspaceId, strategyId)

    /* -------------------- Success Watcher -------------------- */

    watch(
        [
            succeedCreateExpenseAccountCommandV1,
            succeedUpdateCostAccountCommandV1,
            succeedDeleteExpenseAccountCommandV1
        ],
        ([create, update, del], [prevCreate, prevUpdate, prevDel]) =>
        {
            if (
                (create && !prevCreate) ||
                (update && !prevUpdate) ||
                (del && !prevDel)
            )
            {
                runManageCostsPageQueryV1()
                expenseAccountEditor.close()
            }
        }
    )

    watch(
        [
            succeedCreateCostSubtotalCommandV1,
            succeedRenameCostSubtotalCommandV1,
            succeedDeleteCostSubtotalCommandV1
        ],
        ([create, rename, del], [prevCreate, prevRename, prevDel]) =>
        {
            if (
                (create && !prevCreate) ||
                (rename && !prevRename) ||
                (del && !prevDel)
            )
            {
                runManageCostsPageQueryV1()
                costSubtotalEditor.close()
            }
        }
    )

    /* -------------------- UI Actions -------------------- */

    function showExpenseCreator(parentSubtotalId: string | null)
    {
        expenseAccountEditor.showCreator(parentSubtotalId, subtotals.value)
    }

    function showExpenseEditor(e: CostAccountReadModel)
    {
        expenseAccountEditor.showEditor(
            e.Id,
            e.Name,
            e.Classification,
            e.SubtotalId,
            e.CanEditName,
            e.CanEditClassification,
            subtotals.value
        )
    }

    function showSubtotalEditor(s: CostSubtotalReadModel)
    {
        costSubtotalEditor.showEditor(s.Id, s.Name)
    }

    function showSubtotalCreator()
    {
        costSubtotalEditor.showCreator()
    }

    function handleCreateExpenseAccountCommand(e: CreateExpenseAccountEventPayload)
    {
        invokeCreateExpenseAccountCommandV1({
            id: guid(),
            name: e.name,
            description: null,
            classification: e.classification,
            subtotalId: e.subtotalId
        })
    }

    function hanldeUpdateCostAccountCommand(e: UpdateCostAccountEventPayload)
    {
        invokeUpdateCostAccountCommandV1({
            id: e.id,
            name: e.name,
            description: null,
            classification: e.classification,
            subtotalId: e.subtotalId
        })
    }

    function handleDeleteExpenseAccountCommand(expense: CostAccountReadModel)
    {
        if (!expense.CanDelete)
        {
            alert("Can't delete this type of cost")
            return
        }

        if (confirm("Are you sure?"))
        {
            invokeDeleteExpenseAccountCommandV1({
                id: expense.Id
            })
        }
    }

    function handleCreateCostSubtotalCommand(e: CreateCostSubtotalEventPayload)
    {
        invokeCreateCostSubtotalCommandV1({
            id: guid(),
            name: e.name
        })
    }

    function handleRenameCostSubtotalCommand(e: RenameCostSubtotalEventPayload)
    {
        invokeRenameCostSubtotalCommandV1({
            id: e.id,
            name: e.name
        })
    }

    function handleDeleteCostSubtotalCommand(subtotal: CostSubtotalReadModel)
    {
        if (confirm("Are you sure?"))
        {
            invokeDeleteCostSubtotalCommandV1({
                id: subtotal.Id
            })
        }
    }

    /* -------------------- Lifecycle -------------------- */

    onMounted(() =>
    {
        document.title = "Costs | TransparentChoice"
        runManageCostsPageQueryV1()
    })
</script>

<template>
    <div>
        <CostSubtotalEditor :viewModel="costSubtotalEditor" 
                            @create-subtotal="handleCreateCostSubtotalCommand" 
                            @rename-subtotal="handleRenameCostSubtotalCommand" />

        <ExpenseAccountEditor :viewModel="expenseAccountEditor" 
                              @create-expense="handleCreateExpenseAccountCommand"
                              @update-cost="hanldeUpdateCostAccountCommand" />

        <CorporateNavigation ActivePage="strategy" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Costs</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-outline-secondary mr-2" style="padding: 0.7em 2em;" @click="showSubtotalCreator()">Create subtotal</button>
                    <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="showExpenseCreator(null)">Create expense</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="mt-3">

                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th></th>
                            <th style="width: 120px;"></th>
                            <th style="width: 120px;"></th>
                            <th style="width: 120px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="cost in standaloneCosts" :key="cost.Id">
                            <td>
                                <strong>{{cost.Name}}</strong><br />
                            </td>
                            <td>
                                {{cost.AccountType}}
                            </td>
                            <td>
                                {{cost.Classification}}
                            </td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" @click="showExpenseEditor(cost)">Edit</a>
                                        <a class="dropdown-item" @click="handleDeleteExpenseAccountCommand(cost)">Delete</a>
                                    </div>
                                </div>
                            </td>
                        </tr>

                    </tbody>
                    <tbody v-for="sub in subtotals" :key="sub.Id">
                        <tr>
                            <td colspan="3">
                                {{sub.Name}}
                            </td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" @click="showSubtotalEditor(sub)">Edit</a>
                                        <a class="dropdown-item" @click="handleDeleteCostSubtotalCommand(sub)">Delete</a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="cost in sub.Costs" :key="cost.Id">
                            <td>
                                <strong>{{cost.Name}}</strong><br />
                            </td>
                            <td>
                                {{cost.AccountType}}
                            </td>
                            <td>
                                {{cost.Classification}}
                            </td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" @click="showExpenseEditor(cost)">Edit</a>
                                        <a class="dropdown-item" @click="handleDeleteExpenseAccountCommand(cost)">Delete</a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>              
            </div>
        </div>
    </div>
</template>



